import { useState, useEffect, useMemo } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { initialCMSContent } from '../data/cms';
import { getGoogleDrivePreviewUrl, normalizeVideoUrl } from '../utils/media';
import CTAButton from './CTAButton';
import { useStorefrontData } from '../context/StorefrontDataContext';
import { useLiveEditorScope } from '../context/LiveEditorScopeContext';
import EditableText from './live-editor/EditableText';
import EditableRichText from './live-editor/EditableRichText';
import EditableMedia from './live-editor/EditableMedia';
import { Globe, FileText, EyeOff, Sparkles, Settings, RotateCcw, Eye } from 'lucide-react';

export default function GlobalCTA() {
  const storefront = useStorefrontData();
  const scope = useLiveEditorScope();
  const shouldReduceMotion = useReducedMotion();
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);

  const isAdmin = scope.isAdmin || storefront.isAdmin;
  const isEditMode = scope.isEditMode || storefront.isEditMode;
  const isPreviewMode = scope.isPreviewMode || storefront.isPreviewMode;
  const canEdit = Boolean(isAdmin && isEditMode && !isPreviewMode);

  // Determine if this is a subpage scope (e.g. shop_page, about_page) or main_site (homepage)
  const isSubpage = Boolean(scope.pageKey && scope.pageKey !== 'main_site');
  const pageCta = scope.data?.cta;

  // Mode: 'inherit' (default) | 'custom' | 'hidden'
  const ctaMode = isSubpage ? (pageCta?.mode || 'inherit') : 'inherit';
  const isCustom = isSubpage && ctaMode === 'custom';
  const isHidden = isSubpage && ctaMode === 'hidden';

  // Effective CTA content resolution
  const masterCta = storefront.cms?.cta || initialCMSContent.cta;
  const effectiveCta = useMemo(() => {
    if (isCustom && pageCta) {
      return {
        title: pageCta.title ?? masterCta.title,
        description: pageCta.description ?? masterCta.description,
        buttonText: pageCta.buttonText ?? masterCta.buttonText,
        buttonLink: pageCta.buttonLink ?? masterCta.buttonLink,
        backgroundImageUrl: pageCta.backgroundImageUrl ?? masterCta.backgroundImageUrl,
        backgroundVideoUrl: pageCta.backgroundVideoUrl ?? masterCta.backgroundVideoUrl,
      };
    }
    return masterCta;
  }, [isCustom, pageCta, masterCta]);

  const heroVideoUrl = normalizeVideoUrl(effectiveCta.backgroundVideoUrl || '');
  const heroDrivePreviewUrl = useMemo(() => getGoogleDrivePreviewUrl(heroVideoUrl), [heroVideoUrl]);

  useEffect(() => {
    setHeroVideoFailed(false);
  }, [heroVideoUrl]);

  const heroVideoMimeType = useMemo(() => {
    if (!heroVideoUrl) return undefined;
    const clean = heroVideoUrl.split('?')[0].toLowerCase();
    if (clean.endsWith('.mp4')) return 'video/mp4';
    if (clean.endsWith('.webm')) return 'video/webm';
    if (clean.endsWith('.ogv') || clean.endsWith('.ogg')) return 'video/ogg';
    return undefined;
  }, [heroVideoUrl]);

  // Handler to update fields targeting the right scope
  const handleUpdateField = (field: string, value: any) => {
    if (isSubpage) {
      const existing = scope.data?.cta || {};
      scope.updateSection('cta', {
        ...existing,
        mode: 'custom',
        [field]: value
      });
    } else {
      storefront.updateCmsField('cta', field, value);
    }
  };

  // Convert to custom local CTA for this page
  const handleMakeCustom = () => {
    scope.updateSection('cta', {
      mode: 'custom',
      title: masterCta.title,
      description: masterCta.description,
      buttonText: masterCta.buttonText,
      buttonLink: masterCta.buttonLink,
      backgroundImageUrl: masterCta.backgroundImageUrl,
      backgroundVideoUrl: masterCta.backgroundVideoUrl,
    });
  };

  // Reset back to inherit global master
  const handleResetToMaster = () => {
    if (window.confirm('Kembalikan CTA di halaman ini agar menggunakan Master Global? Perubahan kustom di halaman ini akan digantikan.')) {
      scope.updateSection('cta', {
        mode: 'inherit'
      });
    }
  };

  // Toggle hide/show CTA specifically for this page
  const handleToggleHide = () => {
    const nextMode = isHidden ? 'inherit' : 'hidden';
    scope.updateSection('cta', {
      mode: nextMode
    });
  };

  // If hidden on subpage:
  if (isHidden) {
    if (!canEdit) {
      return null;
    }
    return (
      <section id="cta-section" className="py-12 px-6 bg-amber-50/70 border-2 border-dashed border-amber-300 text-center rounded-2xl my-8 max-w-5xl mx-auto flex flex-col items-center justify-center gap-3 animate-fade-in">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700">
          <EyeOff className="w-5 h-5" />
        </div>
        <div>
          <h4 className="text-sm font-bold text-gray-900 font-serif">Seksi CTA Disembunyikan di Halaman Ini</h4>
          <p className="text-xs text-muted max-w-md mx-auto mt-0.5">
            Pengunjung publik tidak akan melihat banner ini saat membuka halaman {scope.pageTitle || 'ini'}.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
          <button
            type="button"
            onClick={handleToggleHide}
            className="px-3.5 py-1.5 bg-[#722F38] hover:bg-[#5E272E] text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-xs transition-transform active:scale-95 cursor-pointer"
          >
            <Eye className="w-3.5 h-3.5" />
            <span>Tampilkan Kembali (Pakai Master Global)</span>
          </button>
          <button
            type="button"
            onClick={handleMakeCustom}
            className="px-3.5 py-1.5 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold rounded-lg border border-gray-300 shadow-xs transition-colors cursor-pointer"
          >
            Kustomisasi Khusus Halaman Ini
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="cta-section" className="w-full min-h-[100dvh] relative overflow-hidden">
      <div className="absolute inset-0 bg-[#3A3A3A]" />

      {/* Floating Mode Control Bar when in Edit Mode */}
      {canEdit && (
        <div className="absolute top-4 left-4 right-4 z-40 flex flex-wrap items-center justify-between gap-3 pointer-events-auto">
          {isSubpage ? (
            <div className="flex flex-wrap items-center gap-2 bg-black/75 backdrop-blur-md px-3.5 py-2 rounded-xl border border-white/20 text-white shadow-xl">
              <span className="flex items-center gap-1.5 text-[11px] font-mono uppercase font-bold tracking-wider">
                {isCustom ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-blue-300 flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      Kustom Halaman Ini
                    </span>
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    <span className="text-amber-300 flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" />
                      Mewarisi Master Global
                    </span>
                  </>
                )}
              </span>

              <div className="h-4 w-[1px] bg-white/20 mx-1" />

              {isCustom ? (
                <button
                  type="button"
                  onClick={handleResetToMaster}
                  className="px-2.5 py-1 text-[11px] bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/20 flex items-center gap-1 transition-colors cursor-pointer"
                  title="Kembalikan CTA halaman ini ke Master Global"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Reset ke Master</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleMakeCustom}
                  className="px-2.5 py-1 text-[11px] bg-amber-400/25 hover:bg-amber-400/35 text-amber-200 rounded-md border border-amber-300/40 font-semibold flex items-center gap-1 transition-colors cursor-pointer"
                  title="Buat penawaran berbeda khusus untuk halaman ini"
                >
                  <Sparkles className="w-3 h-3" />
                  <span>Kustomisasi CTA Halaman Ini ↗</span>
                </button>
              )}

              <button
                type="button"
                onClick={handleToggleHide}
                className="px-2.5 py-1 text-[11px] bg-white/10 hover:bg-red-500/30 text-white/90 hover:text-red-200 rounded-md border border-white/20 flex items-center gap-1 transition-colors cursor-pointer"
                title="Sembunyikan CTA khusus di halaman ini"
              >
                <EyeOff className="w-3 h-3" />
                <span>Sembunyikan</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-black/75 backdrop-blur-md px-3.5 py-2 rounded-xl border border-amber-400/40 text-white shadow-xl">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-[11px] font-mono uppercase font-bold tracking-wider text-amber-300 flex items-center gap-1">
                <Globe className="w-3.5 h-3.5" />
                Master Global CTA (Default Semua Halaman)
              </span>
            </div>
          )}

          {/* Quick trigger to open Master Drawer */}
          <button
            type="button"
            onClick={() => storefront.openMasterDrawer('cta')}
            className="px-3 py-1.5 bg-black/75 backdrop-blur-md hover:bg-black/90 text-amber-200 hover:text-white rounded-xl border border-white/20 text-xs font-semibold flex items-center gap-1.5 shadow-xl transition-all cursor-pointer"
          >
            <Settings className="w-3.5 h-3.5 text-amber-300" />
            <span>Kelola di Master Drawer ↗</span>
          </button>
        </div>
      )}

      {/* Editable Media Wrapper */}
      <EditableMedia
        currentImageUrl={effectiveCta.backgroundImageUrl || storefront.cms?.hero?.imageUrl || ''}
        currentVideoUrl={effectiveCta.backgroundVideoUrl || ''}
        onImageChange={(url) => handleUpdateField('backgroundImageUrl', url)}
        onVideoChange={(url) => handleUpdateField('backgroundVideoUrl', url)}
        label={isCustom ? "Background CTA (Kustom)" : "Background Master CTA"}
        className="absolute inset-0 w-full h-full"
      >
        {heroDrivePreviewUrl && !shouldReduceMotion ? (
          <iframe
            src={heroDrivePreviewUrl}
            className="absolute inset-0 w-full h-full object-cover"
            allow="autoplay"
            referrerPolicy="no-referrer"
            title="CTA Video"
          />
        ) : heroVideoUrl && !heroVideoFailed && !shouldReduceMotion ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            onError={() => setHeroVideoFailed(true)}
            className="absolute inset-0 w-full h-full object-cover"
            key={heroVideoUrl}
          >
            <source src={heroVideoUrl} {...(heroVideoMimeType ? { type: heroVideoMimeType } : {})} />
          </video>
        ) : (
          <img
            src={
              effectiveCta.backgroundImageUrl ||
              storefront.cms?.hero?.imageUrl ||
              '/hero-image.jpg'
            }
            alt={effectiveCta.title}
            className="absolute inset-0 w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
        )}
        <div className="absolute inset-0 bg-[#3A3A3A]/30" />
        <div className="absolute inset-0 bg-[#722F38]/12" />
      </EditableMedia>

      {/* Foreground Content */}
      <div className="relative z-10 min-h-[100dvh] flex flex-col items-center justify-center text-center px-6 py-24">
        {canEdit ? (
          <div className="w-full max-w-5xl mb-6">
            <EditableRichText
              value={effectiveCta.title}
              onChange={(val) => handleUpdateField('title', val)}
              as="h2"
              multiline
              label={isCustom ? "CTA Headline (Kustom)" : "CTA Headline (Master)"}
              className="text-[#F9F9F9] text-4xl md:text-6xl lg:text-7xl font-serif leading-tight text-center"
            />
          </div>
        ) : (
          <motion.h2
            initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.7, ease: 'easeOut' }}
            className="text-[#F9F9F9] text-4xl md:text-6xl lg:text-7xl font-serif leading-tight max-w-5xl"
            dangerouslySetInnerHTML={{ __html: effectiveCta.title }}
          />
        )}

        {canEdit ? (
          <div className="w-full max-w-4xl mb-10">
            <EditableRichText
              value={effectiveCta.description}
              onChange={(val) => handleUpdateField('description', val)}
              as="p"
              multiline
              label={isCustom ? "CTA Deskripsi (Kustom)" : "CTA Deskripsi (Master)"}
              className="text-[#F9F9F9]/88 text-sm md:text-lg text-center"
            />
          </div>
        ) : (
          <motion.div
            initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.7, delay: 0.08, ease: 'easeOut' }}
            className="text-[#F9F9F9]/88 text-sm md:text-lg mt-6 mb-10 max-w-4xl"
            dangerouslySetInnerHTML={{ __html: effectiveCta.description }}
          />
        )}

        {canEdit ? (
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <EditableText
              value={effectiveCta.buttonText}
              onChange={(val) => handleUpdateField('buttonText', val)}
              as="span"
              label="Teks Tombol CTA"
              className="text-xs font-semibold px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg border border-white/40 cursor-pointer"
            />
            <EditableText
              value={effectiveCta.buttonLink || '/shop'}
              onChange={(val) => handleUpdateField('buttonLink', val)}
              as="span"
              label="Link URL Tombol CTA"
              className="text-xs font-mono text-white/80 px-3 py-1 bg-black/40 rounded border border-white/10 cursor-pointer"
            />
          </div>
        ) : (
          <motion.div
            initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.65, delay: 0.14, ease: 'easeOut' }}
          >
            <CTAButton 
              to={effectiveCta.buttonLink || '/shop'}
              variant="transparent"
            >
              {effectiveCta.buttonText}
            </CTAButton>
          </motion.div>
        )}
      </div>
    </section>
  );
}
